import BaseAPI from './BaseAPI';

export default class AlertManagerAPI extends BaseAPI {
  async send(params: Record<string, unknown>) {
    const {
      senderId,
      workflowId,
      messageId,
      recipientId,
      soundName,
      priority,
      isEscalated = false,
      organizationId,
      alertingSystem,
      messageText,
      orgToken,
      authKey,
      authSecret,
    } = params;

    const resolved = {
      senderId,
      messageId,
      recipientId,
      soundName,
      priority,
      isEscalated,
      organizationId,
      alertingSystem,
      messageText,
    };

    if (areAnyKeysUndefined(resolved)) {
      throw new Error('Invalid Parameters');
    }

    if (!orgToken || !authKey || !authSecret) {
      throw new Error('Missing alert credentials (orgToken, authKey, authSecret)');
    }

    const sanitizedMessageText = sanitizeForXml(messageText as string);
    const securityCode = `${orgToken}:${authKey}:${authSecret}`;
    const headers = {
      'TT-X-Organization-Key': orgToken as string,
      'Content-Type': 'application/xml',
      Accept: 'application/xml',
    };
    const currentDate = new Date();

    const body = `<?xml version="1.0"?>
<!DOCTYPE wctp-Operation SYSTEM "http://dtd.wctp.org/wctp-dtd-v1r3.dtd">
<wctp-Operation wctpVersion="wctp-dtd-v1r3">
    <wctp-SubmitRequest>
        <wctp-SubmitHeader submitTimestamp="${currentDate.toISOString()}" >
            <wctp-Originator senderID="${escapeXml(senderId as string)}" securityCode="${escapeXml(
      securityCode
    )}" />
            <wctp-MessageControl messageID="${escapeXml(
              messageId as string
            )}" isEscalated="${isEscalated}" allowResponse="true" notifyWhenQueued="true" allowTruncation="true" preformatted="true" deliveryPriority="${escapeXml(
      priority as string
    )}" alertingSystem="${escapeXml(alertingSystem as string)}" sound="${escapeXml(
      soundName as string
    )}"${workflowId ? ` workflowID="${escapeXml(workflowId as string)}"` : ''} />
            <wctp-Recipient recipientID="${escapeXml(recipientId as string)}" />
        </wctp-SubmitHeader>
        <wctp-Payload>
            <wctp-MCR>
                <wctp-MessageText>${sanitizedMessageText}</wctp-MessageText>
                <wctp-Choice>ACCEPT</wctp-Choice>
                <wctp-Choice>DECLINE</wctp-Choice>
            </wctp-MCR>
        </wctp-Payload>
    </wctp-SubmitRequest>
</wctp-Operation>
`;

    const { data } = await this.httpClient.post('/v1/integrations/wctp/nurse_call/msg', {
      data: body,
      headers,
    });

    return data;
  }

  async cancel(params: Record<string, unknown>) {
    const {
      senderId,
      workflowId,
      messageId,
      recipientId,
      soundName,
      priority,
      isEscalated = false,
      organizationId,
      alertingSystem,
      messageText,
      actionBy = 'System',
      orgToken,
      authKey,
      authSecret,
    } = params;

    const resolved = {
      senderId,
      messageId,
      recipientId,
      soundName,
      priority,
      isEscalated,
      organizationId,
      alertingSystem,
      messageText,
    };

    if (areAnyKeysUndefined(resolved)) {
      throw new Error('Invalid Parameters');
    }

    if (!orgToken || !authKey || !authSecret) {
      throw new Error('Missing alert credentials (orgToken, authKey, authSecret)');
    }

    const sanitizedMessageText = sanitizeForXml(messageText as string);
    const securityCode = `${orgToken}:${authKey}:${authSecret}`;
    const headers = {
      'TT-X-Organization-Key': orgToken as string,
      'Content-Type': 'application/xml',
      Accept: 'application/xml',
    };
    const currentDate = new Date();

    const body = `<?xml version="1.0"?>
<!DOCTYPE wctp-Operation SYSTEM "http://dtd.wctp.org/wctp-dtd-v1r3.dtd">
<wctp-Operation wctpVersion="wctp-dtd-v1r3">
    <wctp-SubmitRequest>
        <wctp-SubmitHeader submitTimestamp="${currentDate.toISOString()}" >
            <wctp-Originator senderID="${escapeXml(senderId as string)}" securityCode="${escapeXml(
      securityCode
    )}" />
            <wctp-MessageControl messageID="${escapeXml(
              messageId as string
            )}" isEscalated="${isEscalated}" allowResponse="true" notifyWhenQueued="true" allowTruncation="true" preformatted="true" deliveryPriority="${escapeXml(
      priority as string
    )}" alertingSystem="${escapeXml(alertingSystem as string)}" sound="${escapeXml(
      soundName as string
    )}"${
      workflowId ? ` workflowID="${escapeXml(workflowId as string)}"` : ''
    } action="CANCEL" actionBy="${escapeXml(actionBy as string)}" />
            <wctp-Recipient recipientID="${escapeXml(recipientId as string)}" />
        </wctp-SubmitHeader>
        <wctp-Payload>
            <wctp-MCR>
                <wctp-MessageText>${sanitizedMessageText}</wctp-MessageText>
                <wctp-Choice>ACCEPT</wctp-Choice>
                <wctp-Choice>DECLINE</wctp-Choice>
            </wctp-MCR>
        </wctp-Payload>
    </wctp-SubmitRequest>
</wctp-Operation>
`;

    const { data } = await this.httpClient.post('/v1/integrations/wctp/nurse_call/msg', {
      data: body,
      headers,
    });

    return data;
  }
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function sanitizeForXml(text: string): string {
  return escapeXml(
    text
      .replace(/\u2014/g, '-') // em dash → hyphen
      .replace(/\u2013/g, '-') // en dash → hyphen
      .replace(/\u2018/g, "'") // left single quote
      .replace(/\u2019/g, "'") // right single quote
      .replace(/\u201C/g, '"') // left double quote
      .replace(/\u201D/g, '"') // right double quote
      .replace(/\u2026/g, '...') // ellipsis
      .replace(/[^\x20-\x7E\n\r\t]/g, '') // strip remaining non-ASCII
  );
}

function areAnyKeysUndefined(obj: Record<string, unknown>) {
  const keysToCheck = [
    'senderId',
    'messageId',
    'recipientId',
    'soundName',
    'priority',
    'isEscalated',
    'organizationId',
    'alertingSystem',
    'messageText',
  ];

  for (const key of keysToCheck) {
    if (obj[key] === undefined || obj[key] === null) {
      return true;
    }
  }

  return false;
}
